import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SharedModule } from 'primeng/api';
import { VButtonV2Component } from 'src/app/core/components/ui-kit/buttons/button-v2/button.component';
import { VDropdownComponent } from 'src/app/core/components/ui-kit/dropdown/dropdown.component';
import { VInputFieldComponent } from 'src/app/core/components/ui-kit/input-field/input-field.component';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { SettingsStateModule } from 'src/app/store/settings/settings.state.module';
import { VBottomGradientWrapperComponent } from '../components/bottom-gradient/bottom-gradient.component';

@Component({
  selector: 'app-onboarding-step-locale',
  imports: [
    SharedModule,
    VInputFieldComponent,
    ReactiveFormsModule,
    VDropdownComponent,
    SettingsStateModule,
    TranslocoModule,
    VBottomGradientWrapperComponent,
    VButtonV2Component,
  ],
  templateUrl: './locale.component.html',
})
export class LocaleSetupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);
  private translate = inject(TranslationService);
  private router = inject(Router);

  createOrganizationError?: string;
  public isNextStepLoading = false;

  newOrgForm = new FormGroup({
    orgName: new FormControl('', Validators.required),
  });
  localeForm = this.fb.group({
    language: [this.getDefaultLanguage(), [Validators.required]],
    timezone: [this.getDefaultTimezone(), [Validators.required]],
  });

  ngOnInit(): void {
    this.localeForm.valueChanges.subscribe(values => {
      if (values.language) {
        this.transloco.setActiveLang(values.language);
      }
    });
  }

  private getDefaultLanguage(): string {
    return this.translate.getDefaultLocale();
  }

  private getDefaultTimezone(): string {
    return this.translate.getDefaultTimezone();
  }

  public languageOptions = [
    { label: this.transloco.translate('locales.en'), value: 'en' },
    { label: this.transloco.translate('locales.de'), value: 'de' },
  ];

  public timezoneOptions = Intl.supportedValuesOf('timeZone').map(timezone => ({
    label: timezone,
    value: timezone,
  }));

  getSelectedLanguageLabel(): string {
    const selectedValue = this.localeForm.get('language')?.value;
    const option = this.languageOptions.find(
      opt => opt.value === selectedValue
    );
    const translatedLabel = option?.label
      ? this.transloco.translate(option.label)
      : null;
    return translatedLabel || selectedValue || this.translate.currentLocale;
  }

  getSelectedTimezoneLabel(): string {
    const selectedValue = this.localeForm.get('timezone')?.value;
    const option = this.timezoneOptions.find(
      opt => opt.value === selectedValue
    );
    return option?.label || selectedValue || this.getDefaultTimezone();
  }

  getCurrentTime(): string {
    const selectedTimezone =
      this.localeForm.get('timezone')?.value || this.getDefaultTimezone();
    const selectedLanguage =
      this.localeForm.get('language')?.value || this.getDefaultLanguage();
    return new Date().toLocaleString(selectedLanguage, {
      timeZone: selectedTimezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  }

  saveLocalePreferences(): void {
    if (this.localeForm.value.language) {
      this.translate.currentLocale = this.localeForm.value.language;
    }
    if (this.localeForm.value.timezone) {
      this.translate.currentTimezone = this.localeForm.value.timezone;
    }

    this.router.navigate(['/organizations/new']);
  }
}
